import React from "react";
import YouTube, { YouTubeProps } from "react-youtube";
import { ObjectID } from "bson";

import FsLightbox from "fslightbox-react";
import {
  HeartIcon,
  Cog8ToothIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  HeartIcon as HeartIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
} from "@heroicons/react/24/solid";
import { BsCheck2Square } from "react-icons/bs";
import Layout from "../../../components/Layout";
import {
  GetVehicleDocument,
  GetVehicleQuery,
  QueryGetVehicleArgs,
  useGetUserLastVisitedQuery,
  useGetUserWatchlistQuery,
  useUpdateLastVisitedMutation,
  Vehicle,
} from "../../../graphql/generated/graphql";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import {
  capitalizeAllWords,
  classNames,
  formatNumber,
  formatPrice,
} from "../../../utils/formatters";
import { initializeApollo, addApolloState } from "../../../graphql/client";
import {
  CATEGORIES,
  CONDITION_KEYS,
  CONDITIONS,
  CURRENCY_KEYS,
  DRIVETRAINS,
  EQUIPMENT,
  FUELS,
  FUEL_KEYS,
  MILEAGE_UNITS,
  MILEAGE_UNIT_KEYS,
  TRANSMISSIONS,
  TIER_KEYS,
  SUBSCRIPTION_TYPES,
  CURRENCIES,
} from "../../../utils/constants";
import Spinner from "../../../components/Spinner";
import { isInWatchlist } from "../../../utils/watchlist";
import useWatchlistMutations from "../../../hooks/useWatchlistMutations";
import { useEffect, useState } from "react";
import SEO from "../../../components/SEO";
import SimilarVehiclesGrid from "../../../components/SimilarVehiclesGrid";
import VerifiedUserIcon from "../../../components/VerifiedUserIcon";
// import HorizontalAdBanner from "../../components/HorizontalAdBanner";
// import SquareAdBanner from "../../components/SquareAdBanner";
import VehicleDetailNav from "../../../components/VehicleDetailNav";
import TierBadge from "../../../components/TierBadge";

import VehicleNotifyButton from "../../../components/VehicleNotifyButton";
import { convertPriceToCRC } from "../../../utils/currency";
import ContactButtons from "../../../components/ContactButtons";
import { fbViewContent } from "../../../lib/fbq";
import Banners from "../../../components/Banners";
import ConnectVehicleBanner from "../../../components/connect/ConnectVehicleBanner";
import ShareSection from "../../../components/ShareSection";
import {
  LoanBanner,
  LoanBannerAccordion,
} from "../../../components/detail/Loan";
// import { canBeFinancedByCrediQ } from "../../../utils/financing";
import APP_CONFIG from "../../../app.config";
import Link from "next/link";
import { getImageVariant, ImageVariant } from "../../../utils/image";

function calculateAproxTransferCost(price) {
  const depreciation = 0.1;
  const transferTax = 0.02;
  const timbres = 0.015;
  const lawyer = 0.02;

  const deprecatedPrice = price - price * depreciation;

  return (
    deprecatedPrice * transferTax +
    deprecatedPrice * timbres +
    deprecatedPrice * lawyer
  );
}

export default function Detail({ data, _id: pathId }) {
  const router = useRouter();
  const { getVehicle: vehicleData } = data || {};
  const {
    _id,
    user,
    brand,
    model,
    price,
    hidePrice,
    category,
    currency,
    year,
    pictures,
    thumbs,
    fuel,
    mileage,
    mileageUnit,
    range,
    rangeMileageUnit,
    cc,
    cylinders,
    kwh,
    soh,
    transmission,
    drivetrain,
    condition,
    color,
    plate,
    location,
    equipment,
    expires,
    listingTier,
    description,
    negotiable,
    receivesAnotherVehicle,
    modelVersion,
    priceIncludesImportTaxes,
    priceIncludesTransfer,
    includesFinancing,
    financingData,
    guarantee,
    guaranteeDescription,
    platePending,
    rtv,
    permit,
    isContactInfoPublic,
    youtubeLink,
    slug,
  } = vehicleData as Vehicle;
  const [hasUpdatedLastVisited, setUpdatedLastVisited] = useState(false);
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 1,
  });
  const [equipmentExpanded, setEquipmentExpanded] = useState(false);

  const { status: sessionStatus, data: sessionData } = useSession();
  const { user: currentUser } = sessionData || {};
  const currentUserId = currentUser?.id;

  const getUserWatchlistResponse = useGetUserWatchlistQuery({
    skip: sessionStatus !== "authenticated",
  });

  const { loading: loadingWatchlist } = getUserWatchlistResponse;

  const [updateLastVisited] = useUpdateLastVisitedMutation({
    onCompleted: () => {
      setUpdatedLastVisited(true);
    },
    update(cache) {
      cache.modify({
        fields: {
          getUserLastVisited(existingLastVisited = [], { readField }) {
            return [
              vehicleData,
              ...existingLastVisited.filter(
                (vehicleRef) => _id !== readField("_id", vehicleRef)
              ),
            ];
          },
        },
      });
    },
  });

  useEffect(() => {
    if (pathId === _id) {
      router.push(
        {
          pathname: `/autosusados/${slug}`,
        },
        undefined,
        { shallow: true }
      );
    }
  }, [pathId]);

  useEffect(() => {
    fbViewContent(vehicleData);
  }, []);

  useEffect(() => {
    return () => {
      const root = document.getElementsByTagName("html")[0];
      root.classList.remove("fslightbox-open");
    };
  }, []);

  const { data: lastVisitedData, loading: loadingLastVisited } =
    useGetUserLastVisitedQuery({
      skip: sessionStatus !== "authenticated" || hasUpdatedLastVisited,
      fetchPolicy: "cache-and-network",
    });

  useEffect(() => {
    if (
      !loadingLastVisited &&
      lastVisitedData &&
      sessionStatus === "authenticated" &&
      !hasUpdatedLastVisited
    ) {
      const lastVisited = lastVisitedData.getUserLastVisited || [];
      const updatedLastVisited = [_id, ...lastVisited.map((item) => item._id)];
      const updatedLastVisitedUnique = [
        ...new Set(updatedLastVisited.slice(0, 8)),
      ];
      updateLastVisited({
        variables: {
          items: updatedLastVisitedUnique,
        },
      });
    }
  }, [
    lastVisitedData,
    loadingLastVisited,
    sessionStatus,
    hasUpdatedLastVisited,
  ]);

  function openLightboxOnSlide(number) {
    setLightboxController({
      toggler: !lightboxController.toggler,
      slide: number,
    });
  }

  const inWatchlist = isInWatchlist(getUserWatchlistResponse, _id);

  // const { brand, model, price, currency, year, pictures } = preloadedData
  //   ? (JSON.parse(preloadedData as string) as Vehicle)
  //   : ({} as Vehicle);

  // const { data, loading, error } = useQuery(GET_VEHICLE, {
  //   variables: {
  //     _id,
  //   },
  //   skip: !_id || !router.isReady,
  // });

  const isCurrentUser = currentUserId === user._id;
  const expired = false; //isExpired(expires); // No vehicle expires now but keeping this just in case

  const {
    addVehicleToWatchlist,
    removeVehicleFromWatchlist,
    addingToWatchlist,
    removingFromWatchlist,
  } = useWatchlistMutations(vehicleData);

  function addToWatchlist(e) {
    e.stopPropagation();
    if (sessionStatus === "unauthenticated") {
      router.push("/auth/signin");
    } else {
      addVehicleToWatchlist();
    }
  }

  function removeFromWatchlist(e) {
    e.stopPropagation();
    if (sessionStatus === "unauthenticated") {
      router.push("/auth/signin");
    } else {
      removeVehicleFromWatchlist();
    }
  }

  const isElectric = fuel === FUEL_KEYS.ELECTRIC;
  const highlightsLeft = [
    {
      label: "Marca",
      value: <span className="uppercase">{brand}</span>,
    },
    {
      label: "Modelo",
      value: (
        <span className="uppercase">
          {model} {modelVersion || ""}
        </span>
      ),
    },
    {
      label: "Año",
      value: year,
    },
    {
      label: "Precio",
      value: hidePrice ? "Precio oculto" : formatPrice(price, currency),
      renderTip: () =>
        hidePrice ? (
          <div className="text-sm text-gray-600 flex flex-col gap-y-2">
            <span className="flex gap-x-1 items-center">
              <InformationCircleIconSolid className="h-4 w-4 text-cyan-400" />{" "}
              El vendedor ha escogido ocultar el precio de este vehículo.
            </span>
          </div>
        ) : (
          <div className="text-sm text-gray-600 flex flex-col gap-y-2">
            {priceIncludesTransfer ? (
              <span className="flex gap-x-1 items-center">
                <BsCheck2Square className="h-4 w-4 text-green-500" /> Incluye
                costos de traspaso
              </span>
            ) : (
              <span className="flex gap-x-1 items-center">
                <InformationCircleIconSolid className="h-4 w-4 text-cyan-400" />{" "}
                Costo aproximado de traspaso:{" "}
                {formatPrice(
                  convertPriceToCRC(
                    calculateAproxTransferCost(price),
                    currency
                  ),
                  CURRENCY_KEYS.CRC
                )}
              </span>
            )}
            {priceIncludesImportTaxes ? (
              <span className="flex gap-x-1 items-center">
                <BsCheck2Square className="h-4 w-4 text-green-500" /> Incluye
                impuestos de nacionalización
              </span>
            ) : null}
          </div>
        ),
    },
    {
      label: "Categoría",
      value: CATEGORIES[category].label,
    },
    {
      label: "Condición",
      value: CONDITIONS[condition].label,
    },
    {
      label: "Ubicación",
      value: `${location.municipality}, ${location.province}`,
    },
    {
      label: "Placa",
      value: platePending ? "Por inscribir" : plate,
    },
    {
      label: "Vendedor",
      value: (
        <div>
          <div className="flex gap-x-1">
            {user.displayName}{" "}
            <VerifiedUserIcon
              idVerified={user.idVerified}
              className="h-5 w-5"
            />
          </div>
        </div>
      ),
    },
  ];

  const highlightsLeftSecondary = [
    {
      label: "Se ofrece financiamiento",
      value: includesFinancing ? "Sí" : "No",
    },
    {
      label: "Negociable",
      value: negotiable ? "Sí" : "No",
    },
    {
      label: "Se recibe vehículo",
      value: receivesAnotherVehicle ? "Sí" : "No",
    },
  ];
  // const highlightsLeftSecondary = !includesFinancing
  // ? [
  //     {
  //       label: "Se ofrece financiamiento",
  //       value: includesFinancing ? "Sí" : "No",
  //     },
  //     {
  //       label: "Negociable",
  //       value: negotiable ? "Sí" : "No",
  //     },
  //     {
  //       label: "Se recibe vehículo",
  //       value: receivesAnotherVehicle ? "Sí" : "No",
  //     },
  //   ]
  // : [
  //     {
  //       label: "Se ofrece financiamiento",
  //       value: includesFinancing ? "Sí" : "No",
  //     },
  //     {
  //       label: "Prima",
  //       value:
  //         financingData &&
  //         `${CURRENCIES[financingData.financingCurrency].symbol}${
  //           financingData.downPayment
  //         }`,
  //     },
  //     {
  //       label: "Mensualidad",
  //       value:
  //         financingData &&
  //         `${CURRENCIES[financingData.financingCurrency].symbol}${
  //           financingData.monthlyFee
  //         }`,
  //     },
  //     {
  //       label: "Plazo",
  //       value: financingData && `${financingData.term} meses`,
  //     },
  //     {
  //       label: "Negociable",
  //       value: negotiable ? "Sí" : "No",
  //     },
  //     {
  //       label: "Se recibe vehículo",
  //       value: receivesAnotherVehicle ? "Sí" : "No",
  //     },
  //   ];

  const highlightsRight = [
    {
      label: "Combustible",
      value: FUELS[fuel].label,
    },
    isElectric
      ? { label: "Autonomía", value: `${range}${rangeMileageUnit}` }
      : null,
    isElectric
      ? {
          label: "Capacidad de la bateria",
          value: `${kwh}kWh`,
        }
      : {
          label: "Cilindraje",
          value:
            cylinders && cc ? (
              <span>
                {cylinders || ""} cilindros {cc || ""}{" "}
                <span className="lowercase">cc</span>
              </span>
            ) : (
              ""
            ),
        },
    isElectric
      ? { label: "Estado de la batería (SoH)", value: soh ? `${soh}%` : "n/d" }
      : null,
    {
      label:
        mileageUnit === MILEAGE_UNIT_KEYS.KILOMETERS
          ? "Kilometraje"
          : "Millaje",
      value: (
        <span>
          {formatNumber(mileage)}
          <span className="lowercase">
            {MILEAGE_UNITS[mileageUnit].labelShort}
          </span>
        </span>
      ),
    },
    {
      label: "Transmisión",
      value: TRANSMISSIONS[transmission].label,
    },
    {
      label: "Tracción",
      value: DRIVETRAINS[drivetrain].label,
    },
    {
      label: "Color exterior",
      value: color.exterior,
    },
    {
      label: "Color interior",
      value: color.interior,
    },
  ];

  const highlightsRightSecondary = [
    {
      label: "Revisión técnica al día",
      value: rtv ? "Sí" : "No",
    },
    {
      label: "Marchamo al día",
      value: permit ? "Sí" : "No",
    },
  ];

  function toggleEquipment() {
    setEquipmentExpanded(!equipmentExpanded);
  }

  function renderColumn(highlights) {
    return highlights
      .filter((h) => h !== null)
      .map((highlight, idx) => (
        <div
          key={highlight.label}
          className={classNames(
            " px-4 py-5 grid grid-cols-2 gap-4 sm:px-6",
            idx % 2 === 0 ? "bg-gray-50" : "bg-white"
          )}
        >
          <dt className="text-sm font-bold text-gray-700">{highlight.label}</dt>
          <dd className="mt-1 text-sm text-gray-900 sm:col-span-1 sm:mt-0 capitalize">
            {highlight.value}
          </dd>
          <div className="col-span-2">
            {highlight.renderTip ? highlight.renderTip() : null}
          </div>
        </div>
      ));
  }

  function renderEquipmentColumn() {
    return equipment
      .filter((eq) => eq !== null)
      .map((key, idx) => (
        <div
          key={key}
          className={classNames(
            " px-4 py-5 grid grid-cols-2 gap-4 sm:px-6",
            idx % 2 === 0 ? "bg-gray-50" : "bg-white"
          )}
        >
          <dd
            className={classNames(
              "mt-1 text-sm text-gray-900 sm:col-span-1 sm:mt-0 flex gap-x-2 items-center",
              EQUIPMENT[key] ? "" : "capitalize"
            )}
          >
            <BsCheck2Square className="h-4 w-4 text-green-500" />
            {EQUIPMENT[key]?.labelEs || key}
          </dd>
        </div>
      ));
  }

  const WatchlistIcon = inWatchlist ? HeartIconSolid : HeartIcon;
  const galleryThumbs = thumbs.filter((t, idx) =>
    youtubeLink ? idx < 7 : idx > 0 && idx < 7
  );

  const SellerSection = ({ className }: { className?: string }) => (
    <div className={className}>
      {expired ? (
        <span className="text-gray-600 text-lg capitalize flex flex-col items-center gap-x-1">
          <ExclamationTriangleIcon className="text-red-500 h-8 w-8" />
          Anuncio expirado
        </span>
      ) : (
        <ContactButtons
          seller={user}
          vehicleInfo={{
            id: _id,
            title: `${brand} ${model} ${year}`,
            slug,
            isContactInfoPublic,
          }}
        />
      )}
    </div>
  );
  const parseVideoId = (url: string) => {
    var regExp =
      /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    var match = url.match(regExp);
    if (match && match[2].length == 11) {
      return match[2];
    } else {
      //error
      return "";
    }
  };

  const opts: YouTubeProps["opts"] = {
    playerVars: {
      // https://developers.google.com/youtube/player_parameters
      autoplay: 1,
      // mute: 1,
    },
  };
  const microdataTags =
    listingTier !== TIER_KEYS.FREE
      ? {
          ["id"]: _id,
          ["og:id"]: _id,
          ["product:id"]: _id,
          ["product:brand"]: brand,
          ["product:availability"]: expired ? "out of stock" : "in stock",
          ["product:condition"]:
            condition === CONDITION_KEYS.NEW ? CONDITION_KEYS.NEW : "used",
          ["product:price:amount"]: price,
          ["product:price:currency"]: currency,
          ["product:retailer_item_id"]: _id,
          ["product:catalog_id"]: "941244453716165",
          // listingTier === TIER_KEYS.MAX
          //   ? "912289993360057"
          //   : "1607936366384349",
          ["product:category"]: "5614",
          ["product:custom_label_0"]: listingTier,
        }
      : null;

  const shareLinkImageRectangular = getImageVariant(
    pictures[0],
    ImageVariant.SHARE_RECTANGULAR
  );
  // generateShareLinkImage({
  //   title: capitalizeAllWords(
  //     `${brand} ${model} • ${year} • ${formatPrice(price, currency)}`
  //   ),
  //   overlayPublicId:
  //     listingTier === TIER_KEYS.COLLECTION
  //       ? OVERLAY_PUBLIC_IDS_LANDSCAPE.collection
  //       : OVERLAY_PUBLIC_IDS_LANDSCAPE.movi,
  //   underlayImageUrl: getCarMediumVariant(pictures[0]),
  // });

  const shareLinkImageSquare = getImageVariant(
    pictures[0],
    ImageVariant.SHARE_SQUARE
  );
  // generateShareLinkImage({
  //   title: capitalizeAllWords(
  //     `${brand} ${model} • ${year} • ${formatPrice(price, currency)}`
  //   ),
  //   overlayPublicId:
  //     listingTier === TIER_KEYS.COLLECTION
  //       ? OVERLAY_PUBLIC_IDS.collection
  //       : OVERLAY_PUBLIC_IDS.movi,
  //   underlayImageUrl: getCarMediumVariant(pictures[0]),
  //   square: true,
  // });

  return (
    <div className="bg-white">
      <SEO
        currentURL={`${process.env.NEXT_PUBLIC_ROOT_URL}/autosusados/${_id}`}
        title={capitalizeAllWords(
          `${brand} ${model} ${
            modelVersion ? `${modelVersion} ` : ""
          }${year} ${formatPrice(price, currency)}`
        )}
        // description={description}
        description="Movi es el sitio web más fácil y seguro para comprar y vender autos usados en Costa Rica. Publicá tu auto de forma gratuita y empezá a recibir ofertas."
        siteTitle="Movi Autos"
        previewImage={shareLinkImageRectangular}
        previewImageSquare={shareLinkImageSquare}
        microdata={microdataTags}
      />
      {/* contains edit and delete buttons  */}
      <VehicleDetailNav
        data={vehicleData}
        isCurrentUser={isCurrentUser}
        title={`${brand} ${model} ${
          modelVersion ? `${modelVersion} ` : ""
        }${year}`}
        expired={expired}
      />
      <div className="pb-6 ">
        {/* Image gallery */}
        <div className="  max-w-3xl mx-auto px-4 md:max-w-7xl grid grid-cols-6 gap-x-2 gap-y-2">
          <FsLightbox
            key={`gallery-${_id}`}
            toggler={lightboxController.toggler}
            slide={lightboxController.slide}
            sources={pictures.map((p) =>
              getImageVariant(p, ImageVariant.CAR_GALLERY, "/carthumbnail")
            )}
            thumbs={thumbs}
            showThumbsOnMount={true}
            exitFullscreenOnClose={true}
            customAttributes={pictures.map((p, index) => ({
              alt: `Imagen ${brand} ${model} ${year} ${index}`,
            }))}
          />
          <div className=" aspect-w-3 aspect-h-2 sm:rounded-lg overflow-hidden col-span-6 md:col-span-5 ">
            {youtubeLink ? (
              <YouTube
                videoId={parseVideoId(youtubeLink)}
                id="https://www.youtube.com/watch?v=h1zLTso9hSg"
                iframeClassName="w-full h-full"
                opts={opts}
              />
            ) : (
              <Image
                fill
                style={{
                  objectFit: "cover",
                }}
                sizes="(max-width: 768px) 100vw, 80vh"
                src={getImageVariant(pictures[0], ImageVariant.CAR_GALLERY)}
                alt={`${brand} ${model} ${year} Image 1`}
                className="w-full h-full object-center object-cover cursor-pointer hover:opacity-75"
                onClick={() => openLightboxOnSlide(1)}
                priority
              />
            )}

            {/* <div className="w-full h-full">
              <div className="absolute right-2 top-2">
                <TierBadge
                  tier={listingTier}
                  large
                  className="drop-shadow-md"
                />
              </div>
            </div> */}
          </div>
          <div className="grid grid-cols-6 md:grid-cols-1 gap-y-2 gap-x-2 col-span-6 md:col-span-1">
            {galleryThumbs.map((thumb, idx) => (
              <div
                key={thumb}
                className={classNames(
                  "relative aspect-w-1 md:aspect-w-2 aspect-h-1 sm:rounded-lg overflow-hidden",
                  idx > 3 ? "block md:hidden" : ""
                )}
              >
                {(idx === 3 || idx === 5) && galleryThumbs.length > 4 ? (
                  <div
                    className={classNames(
                      "absolute flex w-full h-full items-center justify-center z-10 bg-opacity-50 hover:bg-opacity-30 bg-black rounded-md cursor-pointer",
                      idx === 5 ? "flex md:hidden" : "",
                      idx === 3 ? "md:flex hidden" : ""
                    )}
                    onClick={() => openLightboxOnSlide(idx + 2)}
                  >
                    <p className="text-white font-normal text-2xl">
                      +{thumbs.length - (idx + 1)}
                    </p>
                  </div>
                ) : null}
                <Image
                  fill
                  style={{
                    objectFit: "cover",
                  }}
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                  src={galleryThumbs[idx]}
                  alt={`${brand} ${model} ${year} Image ${idx + 2}`}
                  className="w-full h-full object-center object-cover cursor-pointer hover:opacity-75"
                  onClick={() => openLightboxOnSlide(idx + 2)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:max-w-7xl">
          <div className="pt-4 pb-4 mt-3 border-b border-gray-300 ">
            <div className="flex">
              <h1 className="capitalize text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:tracking-tight gap-y-4 gap-x-2">
                {brand} {model} {modelVersion ? `${modelVersion} ` : ""}
                {year}
                {!hidePrice ? <span> • </span> : null}
                {!hidePrice ? (
                  <span className="inline-block mr-2">
                    {formatPrice(price, currency)}
                  </span>
                ) : null}
                {/* <span className="inline break-words"> */}
                {/* </span> */}
              </h1>
              <div className="inline">
                <TierBadge tier={listingTier} large />
              </div>
            </div>

            <ConnectVehicleBanner vehicle={vehicleData} />

            {currentUser?.subscription?.type !== SUBSCRIPTION_TYPES.connect ? (
              <div className="mt-3">
                <p className="text-sm text-gray-500 mb-2">Compartir en redes</p>
                <ShareSection
                  shareUrl={`${process.env.NEXT_PUBLIC_ROOT_URL}/autosusados/${vehicleData.slug}`}
                  originalSeller={user}
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-x-3 gap-y-3 mt-3">
              <div className="flex items-center">
                <div className="flex items-center">
                  {addingToWatchlist || removingFromWatchlist ? (
                    <Spinner className="h-5 w-5" />
                  ) : (
                    <WatchlistIcon
                      className={classNames(
                        "text-gray-900",
                        "h-5 w-5 flex-shrink-0"
                      )}
                      aria-hidden="true"
                    />
                  )}
                </div>
                <p className="sr-only">
                  {inWatchlist
                    ? "Eliminar de favoritos"
                    : "Agregar a favoritos"}
                </p>
                <button
                  onClick={inWatchlist ? removeFromWatchlist : addToWatchlist}
                  disabled={
                    loadingWatchlist ||
                    addingToWatchlist ||
                    removingFromWatchlist
                  }
                  className="ml-2 text-sm font-medium text-orange-500 hover:text-orange-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {inWatchlist
                    ? "Eliminar de favoritos"
                    : "Agregar a favoritos"}
                </button>
              </div>

              <VehicleNotifyButton queryObject={vehicleData} />
              {/* <ShareButton /> */}
            </div>
          </div>
        </div>

        <div className="pt-4 px-4 block lg:hidden">
          <SellerSection className="mb-4" /> 
          {/* {!expired ? <LoanCalculator vehicle={vehicleData} /> : null} */}
          {!expired ? <LoanBannerAccordion vehicle={vehicleData} /> : null}
        </div>
        {/* Product info */}
        <div className="max-w-3xl mx-auto py-4 px-4 md:max-w-7xl md:grid md:grid-cols-3 md:grid-rows-[auto,auto,1fr] md:gap-x-8">
          <div className="col-span-3 lg:col-span-2">
            <div className="overflow-hidden bg-white shadow sm:rounded-lg border border-gray-200">
              <div className="px-4 py-5 flex items-center gap-x-2">
                <InformationCircleIcon className="h-6 w-6" />
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Información del vehículo
                </h3>
                {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  Personal details and application.
                </p> */}
              </div>
              <div className="border-t border-gray-200 grid lg:grid-cols-2 divide-x divide-gray-200">
                <dl className="col-span-2 sm:col-span-1">
                  {renderColumn(highlightsLeft)}
                </dl>
                <dl className="col-span-2 sm:col-span-1">
                  {renderColumn(highlightsRight)}
                </dl>
                <dl className="col-span-2 sm:col-span-1 border border-t-gray-200">
                  {renderColumn(highlightsLeftSecondary)}
                </dl>
                <dl className="col-span-2 sm:col-span-1 border border-t-gray-200">
                  {renderColumn(highlightsRightSecondary)}
                </dl>
                {guarantee ? (
                  <div className="col-span-2">
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-4 sm:px-6">
                      <dt className="text-sm font-bold text-gray-700">
                        Garantía
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:col-span-3 sm:mt-0">
                        {guaranteeDescription}
                      </dd>
                    </div>
                  </div>
                ) : null}
                {description ? (
                  <div className="col-span-2">
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-4 sm:px-6">
                      <dt className="text-sm font-bold text-gray-700">
                        Más información
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:col-span-3 sm:mt-0">
                        {description}
                      </dd>
                    </div>

                    {/* <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-4  sm:px-6">
                    <dt className="text-sm font-bold text-gray-700">
                      Attachments
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:col-span-3 sm:mt-0">
                      <ul
                        role="list"
                        className="divide-y divide-gray-200 rounded-md border border-gray-200"
                      >
                        <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                          <div className="flex w-0 flex-1 items-center">
                            <PaperClipIcon
                              className="h-5 w-5 flex-shrink-0 text-gray-400"
                              aria-hidden="true"
                            />
                            <span className="ml-2 w-0 flex-1 truncate">
                              registro_mantenimiento.pdf
                            </span>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            <a
                              href="#"
                              className="font-medium text-orange-500 hover:text-orange-600"
                            >
                              Download
                            </a>
                          </div>
                        </li>
                        <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                          <div className="flex w-0 flex-1 items-center">
                            <PaperClipIcon
                              className="h-5 w-5 flex-shrink-0 text-gray-400"
                              aria-hidden="true"
                            />
                            <span className="ml-2 w-0 flex-1 truncate">
                              RTV.pdf
                            </span>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            <a
                              href="#"
                              className="font-medium text-orange-500 hover:text-orange-600"
                            >
                              Download
                            </a>
                          </div>
                        </li>
                      </ul>
                    </dd>
                  </div> */}
                  </div>
                ) : null}
              </div>
            </div>

            {equipment?.length ? (
              <div className="overflow-hidden bg-white shadow sm:rounded-lg border border-gray-200 mt-3 mb-4">
                <div className="px-4 py-5 flex items-center gap-x-2">
                  <Cog8ToothIcon className="h-6 w-6" />
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Equipamiento
                  </h3>
                </div>
                <div className="border-t border-gray-200 grid grid-cols-1 divide-x divide-gray-200 ">
                  <div
                    className={classNames(
                      "overflow-scroll transition-all",
                      equipmentExpanded ? " max-h-[700px]" : "max-h-60"
                    )}
                  >
                    <dl className="col-span-1">{renderEquipmentColumn()}</dl>
                  </div>
                  <button
                    onClick={toggleEquipment}
                    className="text-cyan-500 hover:text-cyan-400 cursor-pointer font-medium my-2"
                  >
                    {equipmentExpanded ? "Ver menos -" : "Ver mas +"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-4 md:mt-0 md:row-span-3 col-span-3 lg:col-span-1 hidden lg:block">
            <SellerSection className="mb-4" />

            {!expired ? <LoanBanner vehicle={vehicleData} /> : null}

            {/* {listingTier !== TIER_KEYS.COLLECTION ? (
              <Banners singleBanner />
            ) : null} */}
            {APP_CONFIG.partners.somit.enabled ? (
              <div className="relative mt-3 w-full min-h-60 bg-gray-200 aspect-1  overflow-hidden group-hover:opacity-75 ">
                <div className="h-full">
                  <Link
                    href="/somit"
                    //  onClick={() => trackBannerClick(trackId)}
                  >
                    <div className="absolute h-full w-full">
                      <Image
                        fill
                        style={{
                          objectFit: "cover",
                        }}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        src={"/partners/banner-somit-mobile.jpg"}
                        alt={`banner`}
                        className={classNames(
                          "w-full h-full object-center object-cover rounded-md"
                        )}
                      />
                    </div>
                  </Link>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {/* <Grid
        items={listItems}
        title="Otros vehículos de este vendedor (mostrar solo cuando vendedor es un negocio)"
      /> */}
      <SimilarVehiclesGrid vehicle={vehicleData} />

      {/* <div className="max-w-3xl mx-auto py-4 px-4 md:max-w-7xl">
        <HorizontalAdBanner />
      </div> */}
    </div>
  );
}

Detail.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export const getServerSideProps = async ({ params }) => {
  const apolloClient = initializeApollo({});
  try {
    const { _id } = params;

    const variables: QueryGetVehicleArgs = {};

    if (ObjectID.isValid(_id)) {
      variables._id = _id;
    } else {
      variables.slug = _id;
    }

    const { data } = await apolloClient.query<GetVehicleQuery>({
      query: GetVehicleDocument,
      variables,
    });

    const { getVehicle } = data;

    if (!getVehicle) {
      return {
        notFound: true,
      };
    }

    return addApolloState(apolloClient, {
      props: {
        data,
        _id,
      },
    });
  } catch (e) {
    console.log("ERR", e);
    return {
      notFound: true,
    };
  }
};
